import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Loader2, CheckCheck, ShoppingBag, ShieldCheck, CreditCard, GraduationCap,
  Crown, Lock, MessageSquare, Tag, BadgeCheck, ChevronLeft,
} from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { haceCuanto } from "../lib/conexion";
import { toast } from "sonner";

const TIPO_META = {
  venta: { Icon: ShoppingBag, color: "#16A34A", label: "Pedido" },
  compra: { Icon: ShoppingBag, color: "#16A34A", label: "Pedido" },
  pedido: { Icon: ShoppingBag, color: "#16A34A", label: "Pedido" },
  moderacion: { Icon: ShieldCheck, color: "#CA8A04", label: "Moderación" },
  pago: { Icon: CreditCard, color: "#2563EB", label: "Pago" },
  ajuste: { Icon: CreditCard, color: "#2563EB", label: "Pago" },
  retiro: { Icon: CreditCard, color: "#2563EB", label: "Pago" },
  curso: { Icon: GraduationCap, color: "#7E22CE", label: "Academia" },
  academia: { Icon: GraduationCap, color: "#7E22CE", label: "Academia" },
  membresia: { Icon: Crown, color: "#D97706", label: "Membresía" },
  plan: { Icon: Crown, color: "#D97706", label: "Membresía" },
  seguridad: { Icon: Lock, color: "#DC2626", label: "Seguridad" },
  mensaje: { Icon: MessageSquare, color: "#0891B2", label: "Mensaje" },
  oferta: { Icon: Tag, color: "#EA580C", label: "Oferta" },
  verificacion: { Icon: BadgeCheck, color: "#059669", label: "Verificación" },
};
const meta = (t) => TIPO_META[t] || { Icon: Bell, color: "#475569", label: "Aviso" };

export default function NotificacionesPage() {
  const [data, setData] = useState(undefined);
  const [marcando, setMarcando] = useState(false);

  const cargar = async () => {
    try {
      const { data: d } = await api.get("/notificaciones", { params: { limite: 100 } });
      setData(d);
    } catch { setData(null); }
  };
  useEffect(() => { cargar(); }, []);

  const marcarLeidas = async () => {
    setMarcando(true);
    try {
      await api.post("/notificaciones/marcar-leidas");
      await cargar();
      toast.success("Notificaciones marcadas como leídas.");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setMarcando(false); }
  };

  return (
    <div className="px-5 md:px-8 py-8 max-w-4xl" data-testid="notificaciones-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>

      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3">
            <Bell size={30} style={{ color: "#DC2626" }} /> Notificaciones
          </h1>
          <p className="mt-2 text-slate-600">Alertas de pedidos, moderación, pagos, cursos, membresía y seguridad.</p>
        </div>
        {data?.no_leidas > 0 && (
          <button onClick={marcarLeidas} disabled={marcando} data-testid="notif-marcar-leidas"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-60">
            {marcando ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />} Marcar todas como leídas
          </button>
        )}
      </div>

      <div className="mt-6 space-y-2">
        {data === undefined && (
          <div className="flex items-center gap-2 text-slate-500 py-16"><Loader2 className="animate-spin" size={16} /> Cargando notificaciones…</div>
        )}
        {data === null && <p className="text-sm text-slate-400 py-10">No se pudieron cargar las notificaciones.</p>}
        {data && data.items?.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="notif-vacio">
            <Bell size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">No tienes notificaciones por ahora.</p>
          </div>
        )}
        {data?.items?.map((n) => {
          const m = meta(n.tipo);
          const Icon = m.Icon;
          const Wrapper = n.enlace ? Link : "div";
          const props = n.enlace ? { to: n.enlace } : {};
          return (
            <Wrapper key={n.id} {...props} data-testid={`notif-item-${n.id}`}
              className={`flex items-start gap-3 p-4 rounded-2xl border transition-colors ${n.leido ? "bg-white border-slate-100" : "bg-blue-50/40 border-blue-100"} ${n.enlace ? "hover:border-slate-300 cursor-pointer" : ""}`}>
              <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${m.color}15`, color: m.color }}>
                <Icon size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-slate-900 text-sm">{n.titulo}</p>
                  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: `${m.color}15`, color: m.color }}>{m.label}</span>
                  {!n.leido && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                </div>
                {n.texto && <p className="mt-0.5 text-sm text-slate-600">{n.texto}</p>}
                <p className="mt-1 text-[11px] text-slate-400">{haceCuanto(n.created_at)}</p>
              </div>
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}
